import { fmtInt, fmtMoney, fmtPct } from './format.js';

/**
 * The one-line readings under the dashboard tiles. Each is a plain function
 * of the totals so the arithmetic is unit-tested, and each says nothing
 * rather than something misleading when its denominator is missing.
 */
export interface TileTotals {
  requests: number;
  searches: number;
  monetizedSearches: number;
  paidClicks: number;
  revenue: number;
  coverageSource: 'provider' | 'partly' | 'calculated' | null;
}

/** Requests: how many became a search. */
export function requestsSub(t: Pick<TileTotals, 'requests' | 'searches'>): string {
  return t.requests > 0 ? fmtPct((100 * t.searches) / t.requests) + ' resolved to a search' : '';
}

/** Searches: the daily average over ACTIVE days - days with any traffic at
 * all - not calendar days, so a range that starts before the data does not
 * read as a slump. */
export function searchesSub(
  t: Pick<TileTotals, 'searches'>,
  daily: { requests: number; searches: number; revenue: number; paidClicks: number }[],
): string {
  const active = daily.filter((d) => d.requests > 0 || d.searches > 0 || d.revenue > 0 || d.paidClicks > 0).length;
  return active > 0 ? fmtInt(t.searches / active) + ' / day average' : '';
}

/** Monetized: share of searches. A provider that reports searches=0 with
 * monetized>0 must not print >100%: that is a mapping problem, shown as —. */
export function monetizedSub(t: Pick<TileTotals, 'searches' | 'monetizedSearches'>): string {
  if (t.searches <= 0) return '';
  if (t.monetizedSearches > t.searches) return '— (more monetized than searches reported)';
  return fmtPct((100 * t.monetizedSearches) / t.searches) + ' of searches';
}

/** Revenue: per thousand monetized searches, and per paid click. */
export function revenueSub(t: Pick<TileTotals, 'revenue' | 'monetizedSearches' | 'paidClicks'>): string {
  const parts: string[] = [];
  if (t.monetizedSearches > 0) parts.push(fmtMoney((1000 * t.revenue) / t.monetizedSearches) + ' per 1K monetized');
  if (t.paidClicks > 0) parts.push('$' + (t.revenue / t.paidClicks).toFixed(3) + ' per click');
  if (parts.length) return parts.join(' · ');
  return t.revenue > 0 ? '' : 'no revenue in range';
}

/** Coverage: where the figure comes from. The table marks calculated rows
 * with *; the tile says which kind the range is. */
export function coverageSub(source: TileTotals['coverageSource']): string {
  switch (source) {
    case 'provider':
      return 'provider-reported';
    case 'partly':
      return 'partly provider-reported';
    case 'calculated':
      return 'calculated (monetized ÷ searches)';
    default:
      return '';
  }
}
